import React, { useCallback, useMemo } from "react";
import { Chip, GridLegacy, IconButton, TableCell, TextField, Tooltip, Typography } from "@mui/material";
import TranslationWrapper, { getTranslation } from "../../../components/TranslationWrapper/TranslationWrapper";
import MainNavbar from "../../../components/Navbars/MainNavbar";
import JamezzTableV2, { useReloadState } from "../../../components/JamezzTable/V2/JamezzTableV2";
import FormElement from "../../../components/JamezzTable/FormElement";
import { Link } from "react-router";
import Edit from "@mui/icons-material/Edit";
import useJTFilter from "../../../components/JamezzTable/V2/useJTFilter";
import { Add, FreeBreakfast, Lock } from "@mui/icons-material";
import VestigingenFilter, { getVestigingFilterState } from "../../../components/VestigingFilter/VestigingFilter";
import { connect } from "react-redux";
import GridContainer from "../../../components/Grid/GridContainer";
import GridItem from "../../../components/Grid/GridItem";
import AuthenticationWrapper from "../../../auth/AuthenticationWrapper";
import Button from "../../../components/CustomButtons/Button";
import { unwrap } from "../../../unwrap";
import { Delete } from "@mui/icons-material";
import { _post } from "../../../http";
import AreYouSureDialog from "../../../components/Dialogs/AreYouSureDialog";
import { useDialog } from "../../../licensing/utils/DialogProvider";

const endPoint = "core/arrangements";

function Arrangements(props) {
  const { reloadState, updateReloadState } = useReloadState();
  const [textField, searchFilter] = useJTFilter("name");

  const [openDialog] = useDialog();

  const renderHeaderCells = useCallback(
    () => [
      { id: "id", label: "#", numeric: true, disablePadding: false, sortable: true },
      {
        id: "name",
        label: <TranslationWrapper tDefault={"Arrangement name"} tKey={"arrangements.name"} />,
        numeric: false,
        disablePadding: false,
        sortable: true,
      },
      {
        id: "variant",
        label: <TranslationWrapper tDefault={"Variant"} tKey={"arrangements.variant"} />,
        numeric: false,
        disablePadding: false,
        sortable: true,
      },
      {
        id: "Credits per cover",
        label: <TranslationWrapper tDefault={"Credits"} tKey={"arrangements.rules.credits-per-cover"} />,
        disablePadding: true,
        numeric: true,
      },
      {
        id: "Credits per cover",
        label: <TranslationWrapper tDefault={"Max rounds"} tKey={"arrangements.rules.max-rounds"} />,
        disablePadding: true,
        numeric: true,
      },
      {
        id: "vestiging.naam",
        label: getTranslation("producten.table.vestigingnaam", "Salesarea"),
        numeric: false,
        disablePadding: false,
        sortable: true,
      },
    ],
    []
  );

  const renderRowCells = useCallback((row, isEditable) => {
    const isRegular = row.variant === "regular";
    const colorMapping = {
      regular: "primary",
      supplementary: "info",
      "pre-dining": "success",
    };
    return (
      <>
        <TableCell align={"right"} style={{ width: 100 }}>
          {row.id}
        </TableCell>
        <TableCell>
          {isEditable ? (
            <FormElement endPoint={endPoint} name={"name"} row={row}>
              <TextField size={"small"} variant={"standard"} />
            </FormElement>
          ) : null}
          {!isEditable ? row.name : null}
        </TableCell>
        <TableCell>
          <Chip color={colorMapping[row.variant]} label={row.variant} />
        </TableCell>
        <TableCell align={"right"}>{isRegular ? row.max_number_of_items_per_round_per_cover : null}</TableCell>
        <TableCell align={"right"}>{isRegular ? row.max_rounds : null}</TableCell>
        <TableCell>{row.vestiging?.naam}</TableCell>
      </>
    );
  }, []);

  const renderSelectionActions = useCallback((selected) => {
    return (
      <Tooltip title={<TranslationWrapper tKey={"Delete"} />}>
        <IconButton
          onClick={() => {
            openDialog({
              children: (
                <AreYouSureDialog
                  content={
                    <TranslationWrapper
                      tDefault={"Are you sure you want to delete the selected arrangements?"}
                      tKey={"arrangements.table.delete-multi"}
                    />
                  }
                  onOk={() => {
                    _post("/core/arrangements/multipledelete", { ids: selected }).then(() => {
                      updateReloadState();
                    });
                  }}
                />
              ),
            });
          }}
          style={{ color: "lightcoral" }}
        >
          <Delete />
        </IconButton>
      </Tooltip>
    );
  }, []);

  const renderRowActions = useCallback((row) => {
    return (
      <TableCell align={"right"}>
        <Link to={`./${row.id}/edit`}>
          <Tooltip
            placement={"bottom"}
            title={<TranslationWrapper tDefault={"Edit"} tKey={"global.jamezzTable.edit"} />}
          >
            <IconButton color={"primary"} simple size={"small"}>
              <Edit />
            </IconButton>
          </Tooltip>
        </Link>
      </TableCell>
    );
  }, []);

  const filters = useMemo(() => {
    let filters = {};
    if (searchFilter) {
      filters = searchFilter;
    }
    if (props.filters) {
      filters = { ...filters, ...props.filters };
    }
    return filters;
  }, [searchFilter, props.filters]);

  const salesarea = unwrap(props.vestiging_id);
  const appTarget = unwrap(props.appTarget);

  return (
    <div>
      <MainNavbar paginaTitel={<TranslationWrapper tDefault={"Arrangements"} tKey={"arrangements.table.title"} />} />

      {salesarea ? (
        <GridContainer>
          <GridItem xs={12}>
            {appTarget === "appv5.js" ? (
              <AuthenticationWrapper
                child={
                  <Link to={"./create"}>
                    <Button color={"success"}>
                      <Add />{" "}
                      <TranslationWrapper
                        tDefault={"Create new arrangement"}
                        tKey={"arrangements.createNewArrangement"}
                      />
                    </Button>
                  </Link>
                }
                permissionKey={"arrangements.create"}
              />
            ) : (
              <Typography>
                <Lock />
                <TranslationWrapper tKey={"Only supported in appv5"} />
              </Typography>
            )}
          </GridItem>
        </GridContainer>
      ) : null}
      <VestigingenFilter />
      {textField}
      <GridLegacy container spacing={1}>
        <GridLegacy item xs={12}>
          <JamezzTableV2
            checkboxSelection={true}
            dense={true}
            endPoint={endPoint}
            filters={filters}
            headerIcon={<FreeBreakfast />}
            reloadState={reloadState}
            renderCellsForRow={renderRowCells}
            renderHeaderCells={renderHeaderCells}
            renderRowActions={renderRowActions}
            renderSelectionActions={renderSelectionActions}
            tableTitle={<TranslationWrapper tDefault={"Arrangements"} tKey={"arrangements.table.title"} />}
            uuid={"Arrangements-diu1j209jd89jsx"}
          />
        </GridLegacy>
      </GridLegacy>
    </div>
  );
}

const mapStateToProps = (state) => {
  let filters = getVestigingFilterState(state, true);

  return {
    filters,
    vestiging_id: state.filtersV2.vestiging_id?.value,
    appTarget: state.filtersV2.vestiging_id?.appTarget,
  };
};

export default connect(mapStateToProps)(Arrangements);
